import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { Eye } from "lucide-react";
import { MASKED_AMOUNT } from "utils/format-salary";

type SensitiveNumberInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "value"> & {
  value: string;
};

/**
 * A salary input that hides what it already holds. While it holds a value the
 * viewer has not asked to see, it is a button that looks like the input and
 * reads «•••• €»; clicking it swaps in the real input, focused. An empty input
 * has nothing to hide, and once the viewer types into it, it stays an input.
 */
export default function SensitiveNumberInput({ value, onChange, className, ...inputProps }: SensitiveNumberInputProps) {
  const { t } = useTranslation();
  const [revealed, setRevealed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (revealed) inputRef.current?.focus();
  }, [revealed]);

  if (!revealed && value !== "") {
    return (
      <button
        type="button"
        onClick={() => setRevealed(true)}
        aria-label={t("show_amount")}
        title={t("show_amount")}
        className={clsx(className, "flex items-center justify-between text-left")}
      >
        <span>{MASKED_AMOUNT}</span>
        <Eye size={14} className="text-gray-400" />
      </button>
    );
  }

  return (
    <input
      {...inputProps}
      ref={inputRef}
      className={className}
      value={value}
      onChange={(event) => {
        setRevealed(true);
        onChange?.(event);
      }}
    />
  );
}
